import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Button,
    Space,
    Drawer,
    Form,
    Select,
    AutoComplete,
} from "antd";
import { CloseOutlined, ExportOutlined } from "@ant-design/icons";
import { DRAWER_PLACEMENTS } from "../../../../config/constants";
import styles from "./TableExport.module.css"
import { GroupCode } from "../../../../models/groupcodes/GroupCode";

interface TableExportProps {
    openDrawer: boolean,
    groupCodes: GroupCode[];
    downloadTableData: (groupCode: string, rowCount: number) => void,
    isDownloading: boolean,
    onClose: () => void,
}

const TableExport = ({
    openDrawer,
    groupCodes,
    downloadTableData,
    isDownloading,
    onClose,
}: TableExportProps) => {
    const { t, i18n } = useTranslation("translations");

    const [selectedGroupCode, setSelectedGroupCode] = useState<string>();
    const [selectedRowCount, setSelectedRowCount] = useState<number | null>(100);

    const onDownloadTableDataClick = () => {
        downloadTableData(selectedGroupCode, selectedRowCount);
    }

    return (
        <Drawer
            width={"400px"}
            getContainer={false}
            className={styles.exportDrawer}
            title={t("General.Labels.ExportToExcel", {
                lngs: i18n.languages,
                defaultValue: "Export to excel",
            })}
            placement={DRAWER_PLACEMENTS.BOTTOM}
            onClose={onClose}
            visible={openDrawer}
            closable={false}
            extra={
                <Space>
                <>
                    <Button
                        icon={<ExportOutlined />}
                        size="small"
                        className={"active-btn"}
                        loading={isDownloading}
                        onClick={() => onDownloadTableDataClick()}
                    >
                    {t("General.Labels.Export", {
                        lngs: i18n.languages,
                        defaultValue: "Export",
                    })}
                    </Button>
                    <Button
                        type="ghost"
                        onClick={onClose}
                        icon={<CloseOutlined />}
                        size="small"
                    />
                </>
                </Space>
            }
        >
            <div className={styles.exportDrawerContent}>
                <Form layout="vertical" labelCol={{ span: 12 }}>
                    <Form.Item
                        name="exportGroupCode"
                        className={styles.formItem}
                        label={t(
                            "General.Labels.GroupCode",
                            {
                            lngs: i18n.languages,
                            defaultValue: "Group code",
                            }
                        )}
                    >
                        <Select
                            placeholder={t("General.Placeholders.SelectOption", {
                                lngs: i18n.languages,
                                defaultValue: "Select option",
                            })}
                            loading={groupCodes.length === 0}
                            onChange={(value) => setSelectedGroupCode(value)}
                            showSearch={true}
                            showArrow={true}
                            allowClear={true}
                            options={groupCodes.map((x) => ({
                                label: x.code,
                                value: x.code,
                            }))}
                            filterSort={(optionA, optionB) =>
                                optionA.value! < optionB.value! ? -1 : 1
                            }
                        ></Select>
                    </Form.Item>
                </Form>
                <Form layout="vertical" labelCol={{ span: 12 }}>
                    <Form.Item
                        name="exportNumberOfRows"
                        className={styles.formItem}
                        label="Number of rows:"
                    >
                        <AutoComplete
                            placeholder={t("General.Placeholders.EnterValue", {
                                lngs: i18n.languages,
                                defaultValue: "Enter value",
                            })}
                            onChange={(value) => setSelectedRowCount(value)}
                            showSearch={true}
                            showArrow={true}
                            allowClear={true}
                            defaultValue={100}
                            value={selectedRowCount}
                            options={[
                                { value: null, label: "All" },
                                { value: 100, label: "100" },
                                { value: 500, label: "500" },
                                { value: 1000, label: "1000" },
                            ]}
                        ></AutoComplete>
                    </Form.Item>
                </Form>
            </div>
        </Drawer>
    )
};

export default TableExport;
